import React, { useState, useRef, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { Helmet } from "react-helmet";
import Cookies from "js-cookie";
import {
  SITENAMEALIAS,
  SITENAME,
  LOGO,
  CAPTCHASITEKEY,
  DEMOBROKER,
  WEBSERVICE,
} from "../utils/init";
import { updateFavicon } from "../utils/favicon";
import { showToast, showHttpError } from "../utils/library";
import {
  getBrokerDetails,
  getBrokerDetailsByBrokerId,
  AutoLoginPayments,
  FetchMerchantStatus,
  getAuthToken,
  getAuthUuid,
} from "../utils/service";
import Loader from "../components/Loader";
import { useDispatch } from "react-redux";
import logo from "../assets/images/paybitologo.png";
import axios from "axios";

const AutoLogin = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  // On https://trade.paybito.com we rely on localStorage (not cookies) for
  // auto-login credentials. If any of them are missing, skip the whole
  // auto-login flow entirely and redirect to /login silently.
  const isTradePaybito = window.location.origin === "https://trade.paybito.com";
  const tradeHasCreds =
    !!getAuthToken() &&
    !!localStorage.getItem(SITENAMEALIAS + "_brokerId") &&
    !!getAuthUuid();
  const shouldSkipAutoLogin = isTradePaybito && !tradeHasCreds;

  // State declarations
  const [showLoader, setShowLoader] = useState(true);
  const [error, setError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(
    "Please wait, we are automatically logging you into your exchange..."
  );
  const [exchangeLogo, setExchangeLogo] = useState(
    localStorage.getItem(SITENAMEALIAS + "_exchangeLogo") || ""
  );
  const [exchangeName, setExchangeName] = useState(
    localStorage.getItem(SITENAMEALIAS + "_exchange") || ""
  );
  const [companyName, setCompanyName] = useState(
    localStorage.getItem(SITENAMEALIAS + "_companyName") || ""
  );
  const [backgroundColour, setBackgroundColour] = useState(
    localStorage.getItem(SITENAMEALIAS + "_backgroundColour") || ""
  );
  const [navbarColor, setNavbarColor] = useState(
    localStorage.getItem(SITENAMEALIAS + "_navbarColour") || ""
  );
  const [buttonColor, setButtonColor] = useState(
    localStorage.getItem(SITENAMEALIAS + "_buttonColour") || ""
  );
  const [backgroundImageStatus, setBackgroundImageStatus] = useState(
    Number(localStorage.getItem(SITENAMEALIAS + "_isBackgroundImage")) || 0
  );
  const [backgroundImageLink, setBackgroundImageLink] = useState(
    localStorage.getItem(SITENAMEALIAS + "_backgroundImageLink") || ""
  );

  // Refs

  // Get URL parameters, falling back to cookies from .paybito.com domain
  // Exception: on https://trade.paybito.com, fall back to localStorage instead of cookies
  const getUrlParams = () => {
    const params = new URLSearchParams(location.search);
    const platform = params.get("platform");
    const bId = params.get("brokerId");
    const uId = params.get("uuid");
    const t = params.get("token");

    const isTradePaybito =
      window.location.origin === "https://trade.paybito.com";

    let brokerId, uuid, token;

    if (isTradePaybito) {
      brokerId = bId || localStorage.getItem(SITENAMEALIAS + "_brokerId");
      uuid = uId || getAuthUuid();
      token = t || getAuthToken();
    } else {
      brokerId = bId || Cookies.get("paybito_launch_platform_broker_id");
      uuid = uId || Cookies.get("paybito_launch_platform_uuid");
      token = t || Cookies.get("paybito_launch_platform_token");
    }

    return { brokerId, uuid, token, platform };
  };

  // Wipe the localStorage credentials that the trade.paybito.com flow
  // depends on. Called on every failure path so a stale session can't
  // bounce the user back into /autologin (which causes a blank-screen
  // /login ↔ /autologin loop on this domain).
  const clearTradeCreds = () => {
    localStorage.removeItem(SITENAMEALIAS + "_access_token");
    localStorage.removeItem(SITENAMEALIAS + "_brokerId");
    localStorage.removeItem(SITENAMEALIAS + "_uuid");
  };

  // Handle merchant status and redirect
  const handleMerchantStatus = () => {
    setShowLoader(true);
    let payload = {
      merchant_id: localStorage.getItem(SITENAMEALIAS + "_merchant_id"),
    };
    FetchMerchantStatus(payload)
      .then((res) => {
        setShowLoader(false);
        var response = res.data[0];
        if (response.error != "0") {
          showToast("error", response.error_msg);
        } else {
          localStorage.setItem(
            SITENAMEALIAS + "_email_confirmed",
            response["email_confirmed"]
          );
          localStorage.setItem(
            SITENAMEALIAS + "_basic_verification_submitted",
            response["basic_verification_submitted"]
          );
          localStorage.setItem(
            SITENAMEALIAS + "_crypto_address_added",
            response["crypto_address_added"]
          );
          localStorage.setItem(
            SITENAMEALIAS + "_account_status_id",
            response["account_status_id"]
          );
          const transaction_status_flag = response["transaction_status_flag"];
          const loginFor = localStorage.getItem(
            SITENAMEALIAS + "_login_for"
          );
          const redirectPath = localStorage.getItem(
            SITENAMEALIAS + "_redirect_path"
          );

          if (loginFor) {
            localStorage.removeItem(SITENAMEALIAS + "_login_for");
            let cleanPath = loginFor;
            if (cleanPath.startsWith("/")) {
              cleanPath = cleanPath.substring(1);
            }
            window.location.href = `${window.location.origin}/${cleanPath}`;
          } else if (redirectPath) {
            localStorage.removeItem(SITENAMEALIAS + "_redirect_path");
            history.push(redirectPath);
          } else {
            if (transaction_status_flag === 0) {
              history.push("/get-started");
            } else {
              history.push("/dashboard");
            }
          }
        }
      })
      .catch((err) => {
        setShowLoader(false);
        showHttpError(err);
      });
  };

  const fetchBrokerDetailsByBrokerId = useCallback((brokerId) => {
    if (!brokerId) {
      console.error("No broker ID provided to fetchBrokerDetailsByBrokerId");
      return;
    }

    console.log("Fetching broker details for broker ID:", brokerId);

    getBrokerDetailsByBrokerId(brokerId)
      .then((res) => {
        console.log("Broker details response:", res.data);

        var bakgColor =
          "linear-gradient(90deg, " +
          res.data.color.backgroundColor1 +
          " 0%, " +
          res.data.color.backgroundColor2 +
          " 60%)";

        localStorage.setItem(SITENAMEALIAS + "_backgroundColour", bakgColor);
        localStorage.setItem(
          SITENAMEALIAS + "_navbarColour",
          res.data.color.navbarColor
        );
        localStorage.setItem(
          SITENAMEALIAS + "_buttonColour",
          res.data.color.buttonColor
        );

        localStorage.setItem(
          SITENAMEALIAS + "_isBackgroundImage",
          res.data.color.isBackgroundImage
        );

        localStorage.setItem(
          SITENAMEALIAS + "_backgroundImageLink",
          res.data.color.backgroundImageLink
        );

        setExchangeName(res.data.value[0].exchange);
        setExchangeLogo(res.data.value[0].exchange_logo);
        setCompanyName(res.data.value[0].company_name);
        setBackgroundColour(bakgColor);
        setNavbarColor(res.data.color.navbarColor);
        setButtonColor(res.data.color.buttonColor);
        setBackgroundImageStatus(res.data.color.isBackgroundImage);
        setBackgroundImageLink(res.data.color.backgroundImageLink);

        localStorage.setItem(
          SITENAMEALIAS + "_supportLink",
          res.data.value[0].support_link
        );

        // Always update localStorage with the latest broker details
        localStorage.setItem(
          SITENAMEALIAS + "_companyName",
          res.data.value[0].company_name
        );
        localStorage.setItem(
          SITENAMEALIAS + "_exchange",
          res.data.value[0].exchange
        );
        localStorage.setItem(
          SITENAMEALIAS + "_exchangeLogo",
          res.data.value[0].exchange_logo
        );
        updateFavicon();

        console.log("Broker configuration loaded successfully");
      })
      .catch((err) => {
        console.error("Error fetching broker details by ID:", err);
        showToast("error", "Unable to load broker configuration");
      });
  }, []);

  const getBrokerDetailsP = useCallback(() => {
    const fixedBrokerId = "ATHA24102025105924";
    const urlParams = new URLSearchParams(window.location.search);
    const brokerId =
      Cookies.get("paybito_launch_platform_broker_id") ||
      urlParams.get("broker-id") ||
      urlParams.get("brokerId") ||
      localStorage.getItem(SITENAMEALIAS + "_brokerId") ||
      fixedBrokerId;

    localStorage.setItem(SITENAMEALIAS + "_brokerId", brokerId);
    localStorage.setItem(SITENAMEALIAS + "_broker_id", brokerId);
    localStorage.setItem("DEMOBROKERID", brokerId);

    fetchBrokerDetailsByBrokerId(brokerId);
  }, [fetchBrokerDetailsByBrokerId]);

  const getBrokerDetailsByDomain = useCallback(() => {
    const brokerId = "ATHA24102025105924";
    localStorage.setItem(
      SITENAMEALIAS + "_brokerDomain",
      window.location.origin
    );
    localStorage.setItem(SITENAMEALIAS + "_brokerId", brokerId);
    localStorage.setItem(SITENAMEALIAS + "_broker_id", brokerId);
    fetchBrokerDetailsByBrokerId(brokerId);
  }, [fetchBrokerDetailsByBrokerId]);

  // Handle auto login process
  const handleAutoLogin = async () => {
    try {
      const { brokerId, uuid, token, platform } = getUrlParams();
      console.log("Auto-login parameters:", {
        brokerId,
        uuid,
        token,
        platform,
      });

      // Validate required parameters
      if (!brokerId || !uuid || !token) {
        // setError("Missing required parameters for auto-login");
        // showToast("error", "Missing required parameters for auto-login");
        setShowLoader(false);
        clearTradeCreds();
        history.replace("/login");
        return;
      }

      // Set initial data
      localStorage.setItem(SITENAMEALIAS + "_brokerId", brokerId);
      localStorage.setItem(SITENAMEALIAS + "_uuid", uuid);

      setLoadingMessage("Verifying credentials...");
      const payload = {
        token: `bearer ${token}`,
        uuid: uuid,
        brokerId: brokerId,
      };

      AutoLoginPayments(platform, payload)
        .then((res) => {
          var response = res.data;
          console.log(response);
          if (!response.success) {
            if (response.message === "MERCHANT_NOT_FOUND") {
              history.push("/signup");
              return;
            }
            showToast("error", response.message);
            localStorage.removeItem(SITENAMEALIAS + "_access_token");
            history.push("/login");
          } else {
            setShowLoader(false);
            const merchant = response.merchant;
            const settings = response.settings;

            localStorage.setItem(
              SITENAMEALIAS + "_is_google_auth_enabled",
              settings.GOOGLE_AUTH_ENABLED
            );
            localStorage.setItem(
              SITENAMEALIAS + "_is_phone_auth_enabled",
              settings.PHONE_AUTH_ENABLED
            );

            localStorage.setItem(
              SITENAMEALIAS + "_merchant_id",
              merchant["merchantId"]
            );
            //localStorage.setItem(SITENAMEALIAS + "_is_remember", isRememberMe);
            localStorage.setItem(
              SITENAMEALIAS + "_first_name",
              merchant["firstName"]
            );
            localStorage.setItem(
              SITENAMEALIAS + "_last_name",
              merchant["lastName"]
            );
            localStorage.setItem(SITENAMEALIAS + "_email", merchant["email"]);

            localStorage.setItem(SITENAMEALIAS + "_phoneno", merchant["phone"]);

            let accessToken = response.accessToken;
            let uuid = merchant.uuid;
            console.log("uuid is ", uuid);
            localStorage.setItem(
              SITENAMEALIAS + "_access_token",
              accessToken.accessToken
            );
            localStorage.setItem(SITENAMEALIAS + "_uuid", uuid);
            localStorage.setItem(SITENAMEALIAS + "_merchant_uuid", uuid);
            localStorage.setItem(
              SITENAMEALIAS + "_exchange_uuid",
              response["exchangeUuid"]
            );
            localStorage.setItem(
              SITENAMEALIAS + "_parent_merchant_id",
              response?.merchantBasicDetails?.PARENT_MERCHANT_ID || 0
            );
            localStorage.setItem(SITENAMEALIAS + "_broker_id", brokerId);
            localStorage.setItem(SITENAMEALIAS + "_brokerId", brokerId);
            localStorage.setItem(
              SITENAMEALIAS + "_home_currency",
              response?.homeCurrency
            );
            localStorage.setItem(
              SITENAMEALIAS + "_home_country",
              response?.merchantBasicDetails?.COUNTRY
            );
            localStorage.setItem(
              SITENAMEALIAS + "_access_list",
              JSON.stringify(response["accessList"] || [])
            );
            localStorage.setItem(
              SITENAMEALIAS + "_country",
              response?.merchantBasicDetails?.COUNTRY || ""
            );
            localStorage.setItem("cdforemail", "");
            handleMerchantStatus();
          }
        })
        .catch((err) => {
          setShowLoader(false);
          showHttpError(err);
        });
    } catch (error) {
      console.error("Auto-login error:", error);
      const errorMessage =
        error.response?.data?.error?.error_msg ||
        "Failed to auto-login. Please check your credentials.";
      setError(errorMessage);
      showToast("error", errorMessage);
      setShowLoader(false);
      setTimeout(() => {
        history.push("/login");
      }, 3000);
    }
  };

  // Effects
  useEffect(() => {
    // Capture login-for query parameter if present in URL
    const params = new URLSearchParams(location.search);
    const loginFor = params.get("login-for");
    if (loginFor) {
      localStorage.setItem(SITENAMEALIAS + "_login_for", loginFor);
    }

    // On trade.paybito.com, if localStorage creds are missing, silently
    // redirect to /login without kicking off any API calls or UI flashes.
    const { platform } = getUrlParams();
    console.log("Auto-login platform parameter:", platform);
    if (shouldSkipAutoLogin && (!platform || platform === "")) {
      history.replace("/login");
      return;
    }
    // Get broker details first, then handle auto login
    getBrokerDetailsP();
    handleAutoLogin();
  }, []);

  // Re-sync state from localStorage on mount in case it was populated
  // by a parallel call (Safari can render before async setState commits)
  useEffect(() => {
    const syncFromStorage = () => {
      const storedLogo = localStorage.getItem(SITENAMEALIAS + "_exchangeLogo");
      const storedName = localStorage.getItem(SITENAMEALIAS + "_exchange");
      const storedCompany = localStorage.getItem(
        SITENAMEALIAS + "_companyName"
      );
      if (storedLogo && !exchangeLogo) setExchangeLogo(storedLogo);
      if (storedName && !exchangeName) setExchangeName(storedName);
      if (storedCompany && !companyName) setCompanyName(storedCompany);
    };
    const t = setInterval(syncFromStorage, 500);
    const stop = setTimeout(() => clearInterval(t), 5000);
    return () => {
      clearInterval(t);
      clearTimeout(stop);
    };
  }, []);

  useEffect(() => {
    updateFavicon(exchangeLogo);
  }, [exchangeLogo]);

  // Skip rendering entirely on trade.paybito.com when creds are missing —
  // the effect will redirect to /login. Prevents the auto-login UI flash.
  if (shouldSkipAutoLogin) {
    return null;
  }

  if (showLoader) {
    return (
      <div className="login-wrapper" style={{ background: backgroundColour }}>
        <Loader show={true} message={loadingMessage} />
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <meta charSet="utf-8" />
          <title>
            {exchangeName || localStorage.getItem(SITENAMEALIAS + "_exchange")}
          </title>
        </Helmet>
        <nav
          className="navbar navbar-light extNavbar"
          style={{ backgroundColor: navbarColor }}
        >
          <div className="image-container">
            <img
              src={
                exchangeLogo ||
                localStorage.getItem(SITENAMEALIAS + "_exchangeLogo") ||
                LOGO
              }
              className="logo-cred "
              alt="logo"
            />
          </div>
        </nav>

        {backgroundImageStatus == 0 ? (
          <div
            className="login-wrapper"
            style={{ background: backgroundColour }}
          >
            <br />
            <br />
            <br />
            <br />
            <br />
            <div className="login-background">
              <div className="row mb-2">
                <div className="col-12 col-sm-12 col-xs-12">
                  <div
                    className="card form-card"
                    style={{ borderRadius: "10px" }}
                  >
                    <div className="card-body cardBody text-center">
                      <div className="text-center mb-4">
                        <i
                          className="fa fa-exclamation-circle text-danger"
                          style={{ fontSize: "4rem" }}
                        ></i>
                      </div>
                      <p className="text-center text-danger mb-2">{error}</p>
                      <p className="text-center text-muted small">
                        Redirecting to login page...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {window.location.origin == DEMOBROKER ? (
              <div className="d-flex align-items-end flex-column bd-highlight mb-3 footer-data">
                <a href="https://www.paybito.com/">
                  <div className="btn btn-light mt-auto p-2 bd-highlight footer-data-text">
                    <img
                      src={logo}
                      alt=""
                      style={{ marginRight: 3, height: "30px" }}
                    />
                    Powered by PayBito infrastructure
                  </div>
                </a>
              </div>
            ) : (
              <div></div>
            )}
          </div>
        ) : (
          <div
            className="login-wrapper"
            style={{
              backgroundImage: `url(${backgroundImageLink})`,
              backgroundRepeat: `no-repeat`,
              backgroundSize: `cover`,
              backgroundPosition: `center`,
              minHeight: `100%`,
            }}
          >
            <br />
            <br />
            <br />
            <br />
            <br />
            <div className="login-background">
              <div className="row mb-2">
                <div className="col-12 col-sm-12 col-xs-12">
                  <div
                    className="card form-card"
                    style={{ borderRadius: "10px" }}
                  >
                    <div className="card-body cardBody text-center">
                      <div className="text-center mb-4">
                        <i
                          className="fa fa-exclamation-circle text-danger"
                          style={{ fontSize: "4rem" }}
                        ></i>
                      </div>
                      <p className="text-center text-danger mb-2">{error}</p>
                      <p className="text-center text-muted small">
                        Redirecting to login page...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
};

export default AutoLogin;
